// The 3D view's pointer, asked (docs/claude/handles.md, *The 3D pointer*): one
// probe per kind of pointer3d.ts's order — what lies under a client point; they
// need the view — and every listener that asks it: the press (on the overlay,
// ahead of the camera), the click (`Viewer3D.onSelect`), the canvas's move (the
// cursor, the gizmo's lit arrow, the armed pick's and the tool's previews, the
// outline once a frame), the double-click. Each asks ONCE, and does what the
// answer's row says; none tests a widget or a tool of its own.
import {
  clickOf,
  cursor3dOf,
  focusOf,
  hoverOf,
  type Pointer3dHit,
  pointer3dHitOf,
  pointer3dOf,
  type Pointer3dProbes,
  pressOf,
} from '../pointer3d';
import { type Vec2 } from '../../shared/types';
import {
  armed,
  endArmed,
} from './armedPick';
import {
  state,
  updateStatus,
} from './core';
import {
  handleWork,
  pressWork3d,
  view3dHandleAt,
} from './handleWork';
import {
  gizmoArrowAtClient,
  gizmoUi,
  ringAtClient,
} from './moveGizmo';
import {
  click3dTool,
  hover3dTool,
  leave3dTool,
  pressOwn3d,
  tool3dRow,
} from './tools3d';
import {
  hover3dAt,
  land3dAt,
  readout3dAt,
  select3d,
  viewer3d,
} from './view3d';
import { $ } from '../main';

/** What lies under a client point, one hit test per kind. */
const PROBES: Pointer3dProbes<[number, number]> = {
  armedHandle: () => (handleWork?.space === '3d' ? { armed: handleWork.armed } : null),
  armedPick: () => { const pick = armed(); return pick?.click3d ? { pick } : null; },
  handle: (x, y) => { const handle = view3dHandleAt(x, y); return handle ? { handle } : null; },
  arrow: (x, y) => { const arrow = gizmoArrowAtClient(x, y); return arrow ? { arrow } : null; },
  ring: (x, y) => (ringAtClient(x, y) ? {} : null),
  tool: () => { const row = tool3dRow(); return row ? { row } : null; },
  scene: () => ({}),
};

/** What the 3D pointer is on at a client point (pointer3d.ts's order). */
export function pointer3dAt(clientX: number, clientY: number): Pointer3dHit {
  return pointer3dOf(PROBES, clientX, clientY);
}

/** Where the pointer last was over the 3D view (client px), or null off it, and
 *  the buttons it held. */
export let lastPointer3d: Vec2 | null = null;
let lastButtons3d = 0;
let hoverFrame = 0;

/** The view closed: the pointer is off it. */
export function forgetPointer3d(): void {
  lastPointer3d = null;
}

/** The cursor and the gizmo's lit arrow or ring for what the pointer is on. */
function showPointer3d(hit: Pointer3dHit): void {
  const lit = hoverOf(hit).lit;
  if (lit !== gizmoUi.hover) { gizmoUi.hover = lit; viewer3d?.redraw(); }
  $('view3d-canvas').style.cursor = cursor3dOf(hit);
}

/** What the hover shows where the pointer rests now, with no event — after a
 *  click, a key, a cycle, a tool's mode changed: the outline, the tool's own
 *  preview (the 3D section's plane), the cursor and the lit arrow. */
export function hover3dNow(): void {
  const at = lastPointer3d;
  const hit = at && state.viewTab === '3d' ? pointer3dAt(at.x, at.y) : null;
  const h = hit ? hoverOf(hit) : null;
  hover3dAt(h?.outline ? at : null);
  tool3dRow()?.own?.hover(h?.preview === 'tool' ? at : null);
  if (hit) showPointer3d(hit);
}

/** A press on the 3D view, ahead of the camera (the overlay's capture). */
function press3d(e: PointerEvent, overlay: HTMLElement): void {
  if (state.viewTab !== '3d' || !viewer3d) return;
  const hit = pointer3dAt(e.clientX, e.clientY);
  const press = pressOf(hit, e.button === 0);
  if (press === 'view') return;
  if (press === 'own' && !pressOwn3d(e, pointer3dHitOf(hit, 'tool')?.row ?? null)) return;
  e.preventDefault();
  e.stopPropagation();
  if (press !== 'drop') overlay.setPointerCapture(e.pointerId);
  if (press !== 'own') pressWork3d(hit, e);
}

/** A left click the view was left (`Viewer3D.onSelect`: a press that took
 *  nothing, released where it was): what the 3D pointer says it does there. */
export function click3d(e: PointerEvent): void {
  const hit = pointer3dAt(e.clientX, e.clientY);
  const how = clickOf(hit);
  if (how === 'pick') {
    const p = pointer3dHitOf(hit, 'armedPick')!.pick;
    if (p.click3d!(e.clientX, e.clientY) !== 'keep') endArmed();
  } else if (how === 'tool') {
    click3dTool(e, pointer3dHitOf(hit, 'tool')!.row);
    updateStatus();
  } else if (how === 'select') select3d(e);
  hover3dNow();
}

/** The pointer moving over the 3D view: the cursor and the lit arrow, the armed
 *  pick's or the tool's preview at once, the outline and the readout once a
 *  frame, at the latest position — none while a button is held (an orbit or a
 *  pan is under way). */
function move3d(e: PointerEvent): void {
  lastPointer3d = { x: e.clientX, y: e.clientY };
  lastButtons3d = e.buttons;
  if (state.viewTab !== '3d') return;
  const hit = pointer3dAt(e.clientX, e.clientY), h = hoverOf(hit);
  if (h.preview === 'pick') pointer3dHitOf(hit, 'armedPick')?.pick.move3d?.(e.clientX, e.clientY);
  hover3dTool(e, h.preview === 'tool');
  if (!e.buttons) showPointer3d(hit);
  if (hoverFrame) return;
  hoverFrame = requestAnimationFrame(() => {
    hoverFrame = 0;
    const at = lastPointer3d;
    if (!at || state.viewTab !== '3d') return;
    hover3dAt(!lastButtons3d && hoverOf(pointer3dAt(at.x, at.y)).outline ? at : null);
    readout3dAt(at);
  });
}

/** The 3D view's pointer: its press ahead of the camera (on the overlay, in the
 *  capture phase), its move, its double-click — which moves the camera to orbit
 *  the point clicked, but on what takes its own presses — and leaving it. */
export function installPointer3d(overlay: HTMLElement, canvas3d: HTMLCanvasElement): void {
  overlay.addEventListener('pointerdown', (e) => press3d(e, overlay), true);
  canvas3d.addEventListener('pointermove', move3d);
  canvas3d.addEventListener('dblclick', (e) => {
    if (state.viewTab !== '3d' || !viewer3d) return;
    if (focusOf(pointer3dAt(e.clientX, e.clientY))) viewer3d.focusOn(land3dAt(e.clientX, e.clientY)?.point ?? null);
  });
  canvas3d.addEventListener('pointerleave', () => {
    lastPointer3d = null;
    leave3dTool();
    viewer3d?.setHover(null);
    $('status-coord').textContent = '';
  });
}
